import {optionalMaterialInput, optionalPhysicalMaterialInput} from "#material/nodes/base/connection/validation/parameters"
import type {MaterialNodeDefinition} from "#material/nodes/base/material/types/definition"
import {ExchangeNodeName} from "#material/exchange/node-name"
import {materialClosureOutput} from "#material/nodes/base/material/validation/io"
import {materialUiSchema} from "#material/nodes/base/material/validation/ui"
import type {MaterialNodeSockets} from "#material/types/material-node-sockets"
import {vec3} from "#material/types/vector"
import {color} from "@cm/graph-v3"
import {z} from "zod/v4-mini"

export interface ScannedTransmissionSockets extends MaterialNodeSockets {
    inputs: {
        alpha: "float" // found in inputs: float; found in settings: -; inferred from data: -
        amount: "float" // found in inputs: -; found in settings: float; inferred from data: float
        bsdf: "closure" // found in inputs: closure; found in settings: -; inferred from data: -
        minTransmission: "float" // found in inputs: -; found in settings: float; inferred from data: float
        normal: "normal" // found in inputs: normal; found in settings: -; inferred from data: -
        normalStrength: "float" // found in inputs: -; found in settings: float; inferred from data: float
        transmission: "color" // found in inputs: color; found in settings: -; inferred from data: -
    }
    settings: {
        alpha: "float" // found in inputs: float; found in settings: -; inferred from data: -
        amount: "float" // found in inputs: -; found in settings: float; inferred from data: float
        minTransmission: "float" // found in inputs: -; found in settings: float; inferred from data: float
        normal: "normal"
        normalStrength: "float" // found in inputs: -; found in settings: float; inferred from data: float
        transmission: "color"
    }
    outputs: {
        bsdf: "closure"
    }
}

export const scannedTransmissionDefinition = {
    exchangeFormat: {
        nodeName: ExchangeNodeName.CmScannedTransmission,
    },
    nodeClass: "ScannedTransmission",
    ui: {
        label: "Scanned Transmission",
        color: "#4987af",
        items: [
            {name: "bsdf", valueType: "closure", label: "BSDF", output: true},
            {name: "bsdf", valueType: "closure", label: "BSDF", input: true, setting: false},
            {name: "alpha", valueType: "float", label: "Alpha", input: true, setting: false},
            {name: "normal", valueType: "normal", label: "Normal", input: true, setting: false},
            {name: "transmission", valueType: "color", label: "Transmission", input: true, setting: false, defaultValue: {r: 0.8, g: 0.8, b: 0.8}},
            {name: "normalStrength", valueType: "float", label: "Normal Strength", range: {min: 0, max: 10}, defaultValue: 4, input: true, setting: true},
            {name: "minTransmission", valueType: "float", label: "Min. Transmission", range: {min: 0, max: 1}, defaultValue: 0.2, input: true, setting: true},
            {name: "amount", valueType: "float", label: "Amount", range: {min: 0, max: 1}, defaultValue: 1.0, input: true, setting: true},
        ],
        support: {
            cycles: true,
            three: false,
        },
    },
    validation: {
        returns: z.strictObject({
            bsdf: materialClosureOutput,
        }),
        parameters: z.strictObject({
            inputs: z.strictObject({
                bsdf: optionalPhysicalMaterialInput,
                alpha: optionalMaterialInput,
                normal: optionalMaterialInput,
                transmission: optionalMaterialInput,
                normalStrength: optionalMaterialInput,
                minTransmission: optionalMaterialInput,
                amount: optionalMaterialInput,
            }),
            settings: z.strictObject({
                alpha: z.optional(z.number()),
                amount: z.optional(z.number()),
                minTransmission: z.optional(z.number()),
                normal: z.optional(vec3),
                normalStrength: z.optional(z.number()),
                transmission: z.optional(color),
            }),
            ui: materialUiSchema,
        }),
    },
} satisfies MaterialNodeDefinition<ScannedTransmissionSockets>
